/**
 * useSetCoverPhoto Hook
 * Custom hook for setting a photo as cover
 */

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useAuthStore } from "@/features/auth/store/authStore"
import apiClient from "@/lib/api-client"
import { getProfilePhotos } from "./useProfilePhotos"

async function setCoverPhoto(userId: string, photoId: string) {
  await apiClient.patch(`api/profiles/${userId}/photos/${photoId}/cover`)
}

export function useSetCoverPhoto() {
  const queryClient = useQueryClient()
  const setUser = useAuthStore(state => state.setUser)

  return useMutation({
    mutationFn: ({ userId, photoId }: { userId: string; photoId: string }) =>
      setCoverPhoto(userId, photoId),
    onSuccess: async (_data, variables) => {
      // Invalidate and refetch photos query
      await queryClient.invalidateQueries({ queryKey: ["photos", variables.userId] })

      // Fetch fresh photos from API and update auth store
      const freshPhotos = await getProfilePhotos(variables.userId)
      const user = useAuthStore.getState().user

      if (user) {
        const coverPhoto = freshPhotos.find((p: Record<string, unknown>) => p.isCover === true)
        setUser({
          ...user,
          profile_photos: freshPhotos as never[],
          profilePictureUrl: coverPhoto
            ? ((coverPhoto as Record<string, unknown>).filePath as string)
            : user.profilePictureUrl,
        })
      }
    },
  })
}
